/**
 * Categorías para elegir rápido (puro): las más probables para un movimiento, las recientes y cada categoría en su
 * grupo (variables, fijos, ingresos, no cuentan).
 */
import { categorize, normalizeText, suggestKeyword, DIVIDED, NO_CATEGORY, type Category } from '@pf/domain';
import type { Workbook } from '@pf/workbook';

export type CategoryGroup = 'Gastos variables' | 'Gastos fijos' | 'Ingresos' | 'No cuentan';
export const GROUP_ORDER: CategoryGroup[] = ['Gastos variables', 'Gastos fijos', 'Ingresos', 'No cuentan'];

export const groupOf = (c: Pick<Category, 'kind' | 'nature'>): CategoryGroup =>
  c.kind === 'income' ? 'Ingresos' : c.kind === 'excluded' ? 'No cuentan' : c.nature === 'fixed' ? 'Gastos fijos' : 'Gastos variables';

const real = (name: string) => !!name && name !== NO_CATEGORY && name !== DIVIDED;

/** Las últimas categorías usadas (por fecha del movimiento), sin repetir. */
export function recentCategories(wb: Pick<Workbook, 'movements'>, n = 5): string[] {
  const out: string[] = [];
  for (const m of [...wb.movements].sort((a, b) => b.date.localeCompare(a.date) || b.row - a.row)) {
    if (real(m.category) && !out.includes(m.category)) out.push(m.category);
    if (out.length >= n) break;
  }
  return out;
}

export interface Suggestion { name: string; why: string }

/**
 * Las más probables para un movimiento, en este orden:
 * 1) la que tienen otros movimientos del mismo comercio (la palabra que lo identifica), la más reciente primero;
 * 2) la que dicen tus reglas o las palabras clave de las categorías;
 * 3) las más usadas en los últimos 90 días para gastos (o ingresos, según el importe).
 */
export function suggestCategories(wb: Pick<Workbook, 'movements' | 'rules' | 'categories'>, m: { concept: string; amount: number; account?: string; id?: string }, max = 3): Suggestion[] {
  const known = new Map(wb.categories.map(c => [c.name.toLowerCase(), c]));
  const out: Suggestion[] = [];
  const add = (name: string, why: string) => {
    const c = known.get(name.toLowerCase());
    if (!c || !real(c.name) || out.some(x => x.name === c.name) || out.length >= max) return;
    // Un gasto no se sugiere como ingreso ni al revés (lo que «no cuenta» vale para los dos)
    if ((m.amount < 0 && c.kind === 'income') || (m.amount > 0 && c.kind === 'expense')) return;
    out.push({ name: c.name, why });
  };
  const sameSign = (a: number) => Math.sign(a) === Math.sign(m.amount);
  const kw = suggestKeyword(m.concept);
  if (kw) {
    [...wb.movements].filter(x => x.id !== m.id && sameSign(x.amount) && real(x.category) && normalizeText(x.concept).includes(kw))
      .sort((a, b) => b.date.localeCompare(a.date)).forEach(x => add(x.category, `como otros de ${kw.toLowerCase()}`));
  }
  const r = categorize(wb.rules, wb.categories, m.account ?? '', m.concept, m.amount);
  if (!r.isDefault) add(r.category, 'por tus reglas');
  const last = wb.movements.reduce((d, x) => (x.date > d ? x.date : d), '');
  const since = last ? new Date(Date.parse(last) - 90 * 86400000).toISOString().slice(0, 10) : '';
  const counts = new Map<string, number>();
  wb.movements.filter(x => x.date >= since && sameSign(x.amount) && real(x.category)).forEach(x => counts.set(x.category, (counts.get(x.category) ?? 0) + 1));
  [...counts.entries()].sort((a, b) => b[1] - a[1]).forEach(([name]) => add(name, 'de las que más usas'));
  return out;
}
